/**
 * "Receita recuperada": barras de valor recuperado x abandonado por dia,
 * semana ou mes, agregadas no navegador a partir da serie diaria.
 *
 * Decisoes nao obvias:
 *  1. A granularidade padrao segue a janela do cabecalho (ate 31 dias por
 *     dia, ate 183 por semana, acima por mes) e a escolha manual e descartada
 *     quando a janela muda: 365 barras de "dia" depois de trocar para "12
 *     meses" seria ilegivel sem o lojista ter pedido isso.
 *  2. Abandonado e serie 1 e recuperado e serie 2, na MESMA ordem do
 *     TrendChart logo abaixo: a mesma cor significa a mesma coisa na pagina
 *     inteira.
 *  3. Nenhum seletor de periodo aqui: as abas so mudam o agrupamento; a janela
 *     e sempre a do cabecalho (GUIA-PAINEL, secao 3).
 */

import { useEffect, useId, useMemo, useState } from 'react';
import { ErrorPanel } from '../../../components/PageState';
import { BarChart, Footnote, SectionCard, Tabs, type BarSeries, type TabItem } from '../../../components/ui';
import type { AsyncState } from '../../../lib/api';
import { formatBRLFromCents } from '../../../lib/format';
import type { DailySeries } from '../../../types';
import { agregarSerie, granularidadePadrao, GRANULARIDADE_LABEL, type Granularidade } from '../lib/agregar';

const ABAS: readonly TabItem<Granularidade>[] = [
  { value: 'dia', label: GRANULARIDADE_LABEL.dia },
  { value: 'semana', label: GRANULARIDADE_LABEL.semana },
  { value: 'mes', label: GRANULARIDADE_LABEL.mes },
];

const DESCRICAO: Readonly<Record<Granularidade, string>> = {
  dia: 'Valor recuperado e valor abandonado, somados por dia.',
  semana: 'Valor recuperado e valor abandonado, somados por semana (segunda a domingo).',
  mes: 'Valor recuperado e valor abandonado, somados por mês.',
};

export function ReceitaRecuperada({
  serie,
  onRetry,
  dias,
  className = '',
}: {
  readonly serie: AsyncState<DailySeries>;
  readonly onRetry: () => void;
  /** Dias da janela do cabecalho; `null` em "desde o inicio". */
  readonly dias: number | null;
  readonly className?: string;
}): JSX.Element {
  const [escolha, setEscolha] = useState<Granularidade | null>(null);
  useEffect(() => {
    setEscolha(null);
  }, [dias]);
  const granularidade = escolha ?? granularidadePadrao(dias);
  const painelId = useId();

  const agregada = useMemo(
    () => (serie.status === 'success' ? agregarSerie(serie.data.points, granularidade) : null),
    [serie, granularidade],
  );

  const series: readonly BarSeries[] =
    agregada === null
      ? []
      : [
          { key: 'abandonado', label: 'Abandonado', values: agregada.abandonadoCents, tone: 1 },
          { key: 'recuperado', label: 'Recuperado', values: agregada.recuperadoCents, tone: 2 },
        ];

  return (
    <SectionCard
      id="receita-recuperada"
      title="Receita recuperada"
      icon="moeda"
      description={DESCRICAO[granularidade]}
      aside={<Tabs size="sm" label="Agrupar a receita por" items={ABAS} value={granularidade} onChange={setEscolha} panelId={painelId} />}
      className={className}
    >
      <div id={painelId} role="tabpanel" className="flex flex-col gap-4">
        {serie.status === 'loading' ? <div aria-hidden="true" className="h-56 animate-pulse rounded-xl bg-ink/5" /> : null}
        {serie.status === 'error' ? <ErrorPanel title="Não foi possível carregar a receita por período" error={serie.error} onRetry={onRetry} /> : null}
        {agregada !== null ? (
          agregada.baldes.length === 0 ? (
            <p className="py-10 text-center text-sm text-ink-subtle">Nenhum valor no período: as barras começam vazias.</p>
          ) : (
            <BarChart
              labels={agregada.labels}
              series={series}
              formatValue={formatBRLFromCents}
              height={220}
              title={`Valor recuperado e abandonado por ${GRANULARIDADE_LABEL[granularidade].toLowerCase()}`}
            />
          )
        ) : null}
        <Footnote>
          Recuperado é o valor pago em até 7 dias depois de um contato do Converte Fácil; abandonado é o valor dos checkouts que não fecharam sozinhos.
        </Footnote>
      </div>
    </SectionCard>
  );
}
